import React, { useEffect, useId, useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { BRAND_BLUE } from './trackerUi';
import { TOOLTIP_CARD } from './chartTooltip';
import { useSectionAccent } from './TrackerSection';

/**
 * THE SALESTREKKER HOUSE KIT. One look for every SalesTrekker page (founder, 5 Oct 2026: the File
 * quality page's style, combined with the VALUTTEN stat card, everywhere; inconsistency between
 * tinted and plain panels on one page was the main complaint).
 *
 * HOUSE RULES
 *  1. Every headline figure is a HouseStatCard, laid out by HouseStatGrid (the apps/client 5-card
 *     grid rule, plus 4 and 6 layouts with no orphan).
 *  2. Every panel is a HousePanel: coloured top bar, tinted header, icon chip. Panel (pipelineParts)
 *     and TonePanel (operationsParts) already render through it, so never hand-roll a bordered box.
 *  3. Every period choice is a PeriodBar: label, segmented presets, inline custom dates, filters on
 *     the same row.
 *  4. Every chart colour comes from CHART_PALETTE, with gradientDefs for area fills and donut
 *     segments, grid and axes from CHART_GRID and CHART_AXIS_TICK.
 *  5. Every chart tooltip is the dark card (ChartTooltipCard, or TOOLTIP_CARD for a custom one),
 *     and a bar chart never shows the default white hover box: pass `cursor={CHART_CURSOR}`.
 *
 * NOTHING HERE HOLDS DATA. Every component renders what its caller passes. Icon props are typed as
 * a plain component, not LucideIcon, so the CRA build type-checks them.
 */

export type KitIcon = React.ComponentType<{ className?: string; style?: React.CSSProperties }>;

/** '#2563eb' to '37, 99, 235', for rgba() tints. */
export const hexRgb = (hex: string): string => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

// ---------------------------------------------------------------- tooltip

/**
 * An accessible info tooltip: a focusable button carrying `aria-describedby` to the tooltip text.
 * The text is always in the DOM (visually hidden until hover or focus), so a screen reader hears it
 * and nothing is lost when the tooltip is closed. Escape dismisses it while it is open.
 */
export const InfoTip: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={`About ${label}`}
        aria-describedby={tipId}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        className="rounded-full text-gray-500 transition-colors hover:text-gray-200 focus:outline-none focus-visible:text-gray-100 focus-visible:ring-1 focus-visible:ring-blue-500"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span
        id={tipId}
        role="tooltip"
        data-open={open ? 'true' : 'false'}
        className={
          open
            ? 'absolute left-1/2 top-full z-30 mt-2 w-[min(18rem,70vw)] -translate-x-1/2 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-200 shadow-xl'
            : 'sr-only'
        }
      >
        {children}
      </span>
    </span>
  );
};

// ---------------------------------------------------------------- stat card

export interface HouseStatCardProps {
  label: string;
  /** The figure. A node, so a caller can render an absent value in its own words. */
  value: React.ReactNode;
  /** At most about five words. Anything longer belongs in `info`. */
  sub?: React.ReactNode;
  /** The full explanation, shown in the tooltip beside the label. */
  info?: React.ReactNode;
  icon?: KitIcon;
  /**
   * Accent colour: the top bar, the tint and the icon badge. Each card in a row usually names its
   * own, to tell the figures apart; a card that names none takes its TrackerSection's colour, and
   * brand blue outside one.
   */
  color?: string;
  /** Tints the VALUE only, for figures that carry a warning of their own. */
  tone?: 'plain' | 'warn' | 'alert' | 'good';
  /** An explicit value colour, overriding `tone` (Tile's tone-tinted figures). */
  valueColor?: string;
  /** A small chip beside the label, e.g. the period or the as-at date. */
  chip?: React.ReactNode;
  /** A small control beside the label, e.g. an Open Tasks link. */
  action?: React.ReactNode;
  /** A small picture of the value (a bar, stars), under it. */
  visual?: React.ReactNode;
  /** A line pinned to the bottom of the card, so a row of cards lines up. */
  footer?: React.ReactNode;
  /** Hover title for the whole card. */
  title?: string;
  /** data-tone on the root, for callers and tests that read the tone. */
  dataTone?: string;
  testId?: string;
}

/**
 * The house stat card: tinted gradient body, a thin coloured top accent, a coloured icon badge, an
 * uppercase label with an optional info tooltip, a large value, a short sub-line and an optional
 * footer pinned to the bottom. The content is the first child; the decoration sits in clipped
 * layers after it, so a tooltip can overflow the card.
 */
export const HouseStatCard: React.FC<HouseStatCardProps> = ({
  label, value, sub, info, icon: Icon, color: ownColor, tone = 'plain', valueColor,
  chip, action, visual, footer, title, dataTone, testId = 'info-stat-card',
}) => {
  const section = useSectionAccent();
  const color = ownColor ?? section ?? BRAND_BLUE;
  const rgb = hexRgb(color);
  const toneClass =
    tone === 'alert' ? 'text-red-400'
      : tone === 'warn' ? 'text-amber-400'
        : tone === 'good' ? 'text-emerald-400' : 'text-white';
  return (
    <div
      className="relative flex h-full flex-col rounded-lg border shadow-lg"
      style={{ borderColor: `rgba(${rgb},0.35)` }}
      data-testid={testId}
      data-tone={dataTone}
      data-accent={color}
      title={title}
    >
      <div className={`relative z-10 flex flex-1 flex-col p-5 ${Icon ? 'pr-16' : ''}`}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400" data-testid="stat-label">
            {label}
          </span>
          {info && <InfoTip label={label}>{info}</InfoTip>}
          {chip}
          {action && <span className="ml-auto">{action}</span>}
        </div>
        <div
          className={`mt-2 text-3xl font-bold tracking-tight ${valueColor ? '' : toneClass}`}
          style={valueColor ? { color: valueColor } : undefined}
        >
          {value}
        </div>
        {visual && <div className="mt-2 flex min-h-[1rem] items-center">{visual}</div>}
        {/* Wraps, never truncates: a six-up row is too narrow for one line, and an ellipsis hid the
            words that said what the figure counts (founder, 5 Oct 2026). */}
        {sub && <div className="mt-2 break-words text-xs leading-snug text-gray-400" data-testid="stat-sub">{sub}</div>}
        {footer && <div className="mt-auto pt-2 text-xs leading-snug text-gray-400">{footer}</div>}
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
        <div
          className="absolute inset-0"
          style={{
            background:
              `linear-gradient(135deg, rgba(${rgb},0.16) 0%, rgba(${rgb},0.05) 50%, rgba(17,24,39,0.85) 100%)`,
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-[0.1875rem]"
          style={{ background: `linear-gradient(90deg, transparent, ${color} 15%, ${color} 85%, transparent)` }}
        />
      </div>
      {Icon && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${color}30, ${color}10)`,
            boxShadow: `0 0 1.25rem ${color}20`,
            color,
          }}
        >
          <Icon className="h-5 w-5" />
        </div>
      )}
    </div>
  );
};

/** Earlier name, kept so existing imports work. */
export const InfoStatCard = HouseStatCard;
export type InfoStatCardProps = HouseStatCardProps;

const GRID: Record<3 | 4 | 5 | 6, string> = {
  // apps/client/CLAUDE.md: never lg:grid-cols-5; five sit three over two until 2xl. Rows are equal
  // height from sm up (auto-rows-fr), so a sub-line that wraps to two lines on one card does not
  // leave the next row of cards shorter.
  3: 'grid grid-cols-1 gap-4 sm:auto-rows-fr sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid grid-cols-1 gap-4 sm:auto-rows-fr sm:grid-cols-2 xl:grid-cols-4',
  5: 'grid grid-cols-1 gap-4 sm:auto-rows-fr sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5',
  6: 'grid grid-cols-1 gap-4 sm:auto-rows-fr sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6',
};

/** The stat grid for 3, 4, 5 or 6 cards, each laid out with no orphan on a 14 inch screen. */
export const HouseStatGrid: React.FC<{ count?: 3 | 4 | 5 | 6; testId?: string; children: React.ReactNode }> = ({
  count = 3, testId, children,
}) => <div className={GRID[count]} data-testid={testId}>{children}</div>;

// ---------------------------------------------------------------- panel

export interface HousePanelProps {
  title: string;
  icon?: KitIcon;
  /**
   * Accent colour outside a TrackerSection; brand blue by default. Inside one, the section's colour
   * wins, so the panel always matches the step label above it.
   */
  color?: string;
  /**
   * The colour carries meaning of its own (an amber "approaching due" list beside a red overdue
   * register), so it stands even inside a section. Use sparingly: each one is a panel that does not
   * match its section's step label, and the tab's test names it.
   */
  semantic?: boolean;
  badge?: React.ReactNode;
  blurb?: React.ReactNode;
  /** Controls for this panel only, at the right of the header. */
  actions?: React.ReactNode;
  /** Drop the body padding, for a table that runs edge to edge. */
  flush?: boolean;
  /** A chevron in the header folds the body away. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  dataTone?: string;
  children: React.ReactNode;
}

/**
 * The house panel: a coloured top bar, a header tinted toward the colour, an icon chip, an h3.
 * Inside a TrackerSection it wears the section's colour unless marked `semantic`.
 */
export const HousePanel: React.FC<HousePanelProps> = ({
  title, icon: Icon, color: ownColor, semantic, badge, blurb, actions, flush, collapsible, defaultOpen = true,
  dataTone, children,
}) => {
  const section = useSectionAccent();
  const color = (semantic ? ownColor : section ?? ownColor) ?? BRAND_BLUE;
  const rgb = hexRgb(color);
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const showBody = !collapsible || open;
  return (
    <section
      data-tone={dataTone}
      data-panel-accent={color}
      data-accent-semantic={semantic ? 'true' : undefined}
      data-panel-title={title}
      className="relative overflow-hidden rounded-xl border bg-gray-800/40"
      style={{ borderColor: `rgba(${rgb},0.3)`, boxShadow: `0 0.375rem 1.125rem -0.75rem rgba(${rgb},0.5)` }}
    >
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[0.1875rem]" style={{ background: color }} />
      <header
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-5 pb-3.5 pt-4 ${showBody ? 'border-b border-gray-700/70' : ''}`}
        style={{ background: `linear-gradient(180deg, rgba(${rgb},0.11), rgba(${rgb},0) 100%)` }}
      >
        {Icon && (
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
            style={{ background: `rgba(${rgb},0.18)`, color }}
          >
            <Icon className="h-4 w-4" />
          </span>
        )}
        <h3 className="text-[0.9375rem] font-semibold text-gray-100">
          {collapsible ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              {open
                ? <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                : <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />}
              {title}
            </button>
          ) : title}
        </h3>
        {badge}
        {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
        {blurb && <div className="basis-full text-xs leading-relaxed text-gray-400">{blurb}</div>}
      </header>
      {showBody && <div id={bodyId} className={flush ? '' : 'p-5'}>{children}</div>}
    </section>
  );
};

/** Earlier name, kept so existing imports work. */
export const AccentPanel = HousePanel;

// ---------------------------------------------------------------- chips, bars, banners

export type ChipTone = 'blue' | 'green' | 'amber' | 'red' | 'violet' | 'cyan' | 'muted';

const CHIP: Record<ChipTone, string> = {
  blue: 'border-blue-500/30 bg-blue-500/15 text-blue-200',
  green: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-200',
  amber: 'border-amber-500/30 bg-amber-500/15 text-amber-200',
  red: 'border-red-500/30 bg-red-500/15 text-red-200',
  violet: 'border-violet-500/30 bg-violet-500/15 text-violet-200',
  cyan: 'border-cyan-500/30 bg-cyan-500/15 text-cyan-200',
  muted: 'border-gray-700 bg-white/[0.04] text-gray-400',
};

/** A small tone-coloured count chip. */
export const Chip: React.FC<{ tone?: ChipTone; children: React.ReactNode; className?: string }> = ({
  tone = 'muted', children, className = '',
}) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${CHIP[tone]} ${className}`}
  >
    {children}
  </span>
);

/** A labelled figure chip, for a drawer or row header: "Overdue 3". */
export const FigureChip: React.FC<{ label: string; value: string; tone?: ChipTone }> = ({ label, value, tone = 'muted' }) => (
  <span className={`inline-flex items-baseline gap-1.5 rounded-lg border px-2.5 py-1 text-xs ${CHIP[tone]}`}>
    <span className="font-medium uppercase tracking-wide opacity-80">{label}</span>
    <span className="text-sm font-bold tabular-nums">{value}</span>
  </span>
);

/**
 * A thin horizontal bar, `ratio` from 0 to 1. Purely visual: the figure it draws is always printed
 * beside it, so the bar is hidden from assistive technology.
 */
export const RatioBar: React.FC<{ ratio: number; color: string; className?: string }> = ({ ratio, color, className = '' }) => {
  const width = Math.max(0, Math.min(1, Number.isFinite(ratio) ? ratio : 0)) * 100;
  return (
    <div aria-hidden="true" className={`h-1.5 overflow-hidden rounded-full bg-gray-900/70 ${className}`}>
      <div
        className="h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none"
        style={{ width: `${width}%`, background: color }}
      />
    </div>
  );
};

/** Tinted call-out with an icon and an accent rail. */
export const ToneBanner: React.FC<{ icon: KitIcon; color?: string; children: React.ReactNode }> = ({
  icon: Icon, color = BRAND_BLUE, children,
}) => {
  const rgb = hexRgb(color);
  return (
    <div
      className="relative flex items-start gap-3 overflow-hidden rounded-lg border p-4 pl-5"
      style={{
        borderColor: `rgba(${rgb},0.35)`,
        background: `linear-gradient(90deg, rgba(${rgb},0.16), rgba(${rgb},0.05) 60%, rgba(17,24,39,0.4))`,
      }}
    >
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1" style={{ background: color }} />
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{ background: `rgba(${rgb},0.2)`, color }}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 text-sm text-gray-200">{children}</div>
    </div>
  );
};

// ---------------------------------------------------------------- period bar

export interface PeriodPreset {
  key: string;
  label: string;
}

export const DEFAULT_PERIOD_PRESETS: ReadonlyArray<PeriodPreset> = [
  { key: 'wtd', label: 'WTD' },
  { key: 'mtd', label: 'MTD' },
  { key: 'qtd', label: 'QTD' },
  { key: 'fytd', label: 'FYTD' },
  { key: 'cytd', label: 'CYTD' },
];

export interface PeriodBarProps {
  /** The segments, in order. CUSTOM is appended when `onApplyCustom` is given. */
  presets?: ReadonlyArray<PeriodPreset>;
  selected: string;
  onSelect: (key: string) => void;
  /** The key CUSTOM reports. */
  customKey?: string;
  /** Apply a custom window, both dates inclusive. Omitted hides CUSTOM. */
  onApplyCustom?: (from: string, to: string) => void;
  /** The applied custom window, which the inputs follow. */
  custom?: { from: string; to: string };
  /** Bounds a custom window must sit inside, ISO dates. */
  minDate?: string;
  maxDate?: string;
  /** The label at the left. Null hides it. */
  label?: string | null;
  ariaLabel?: string;
  applyLabel?: string;
  /** Filters (deal type, label) on the same row, after the period. */
  trailing?: React.ReactNode;
}

const PB_HEIGHT = 'h-8';
const PB_SEGMENT = (on: boolean): string =>
  `${PB_HEIGHT} rounded-md px-3 text-xs font-semibold tracking-wide transition-colors ${
    on ? 'bg-blue-600 text-white shadow' : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
  }`;
const PB_INPUT =
  `${PB_HEIGHT} rounded-md border border-gray-600 bg-gray-900/80 px-2.5 text-[0.8125rem] text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`;

/**
 * The one period selector. "Period" at the left, the segmented presets, and, when CUSTOM is chosen,
 * From, To and Apply INLINE on the same row at the same height as the segments. Any filters follow
 * in `trailing`. The row wraps on a narrow screen.
 *
 * A custom window is applied on the button, never on a keystroke: a half-typed year is a valid date
 * the server would answer honestly and uselessly.
 */
export const PeriodBar: React.FC<PeriodBarProps> = ({
  presets = DEFAULT_PERIOD_PRESETS, selected, onSelect, customKey = 'custom', onApplyCustom, custom,
  minDate, maxDate, label = 'Period', ariaLabel = 'Reporting period', applyLabel = 'Apply dates', trailing,
}) => {
  const [draftFrom, setDraftFrom] = useState(custom?.from ?? '');
  const [draftTo, setDraftTo] = useState(custom?.to ?? '');

  // Follow an applied window back into the inputs, so returning to CUSTOM shows the dates on screen.
  useEffect(() => {
    if (custom) {
      setDraftFrom(custom.from);
      setDraftTo(custom.to);
    }
  }, [custom?.from, custom?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const showCustom = onApplyCustom !== undefined && selected === customKey;
  const canApply =
    draftFrom !== '' &&
    draftTo !== '' &&
    draftFrom <= draftTo &&
    (minDate === undefined || draftFrom >= minDate) &&
    (maxDate === undefined || draftTo <= maxDate);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="period-bar">
      {label !== null && <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>}
      <div
        className="inline-flex flex-wrap items-center gap-0.5 rounded-lg border border-gray-700 bg-gray-900/60 p-0.5"
        role="group"
        aria-label={ariaLabel}
      >
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            aria-pressed={p.key === selected}
            onClick={() => onSelect(p.key)}
            className={PB_SEGMENT(p.key === selected)}
          >
            {p.label}
          </button>
        ))}
        {onApplyCustom !== undefined && (
          <button
            type="button"
            aria-pressed={selected === customKey}
            onClick={() => onSelect(customKey)}
            className={PB_SEGMENT(selected === customKey)}
          >
            CUSTOM
          </button>
        )}
      </div>
      {showCustom && (
        <div className="flex flex-wrap items-center gap-2" data-testid="period-bar-custom">
          <span aria-hidden="true" className="text-xs text-gray-400">From</span>
          <input
            type="date"
            aria-label="From"
            className={PB_INPUT}
            value={draftFrom}
            min={minDate}
            max={maxDate}
            onChange={(e) => setDraftFrom(e.target.value)}
          />
          <span aria-hidden="true" className="text-xs text-gray-400">To</span>
          <input
            type="date"
            aria-label="To"
            className={PB_INPUT}
            value={draftTo}
            min={draftFrom || minDate}
            max={maxDate}
            onChange={(e) => setDraftTo(e.target.value)}
          />
          <button
            type="button"
            disabled={!canApply}
            onClick={() => onApplyCustom?.(draftFrom, draftTo)}
            className={`${PB_HEIGHT} rounded-md border border-blue-500/50 bg-blue-600/20 px-3 text-xs font-semibold text-blue-100 transition-colors hover:bg-blue-600/35 disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {applyLabel}
          </button>
        </div>
      )}
      {trailing && <div className="flex flex-wrap items-center gap-3">{trailing}</div>}
    </div>
  );
};

// ---------------------------------------------------------------- charts

/** Ten distinct hues; no two neighbours are both blue-ish. Cycle with chartColour(i). */
export const CHART_PALETTE: ReadonlyArray<string> = [
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#10b981', // emerald
  '#8b5cf6', // violet
  '#f43f5e', // rose
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#d946ef', // fuchsia
  '#0ea5e9', // sky
];

export const chartColour = (index: number): string =>
  CHART_PALETTE[((index % CHART_PALETTE.length) + CHART_PALETTE.length) % CHART_PALETTE.length];

/** Grid, axis and cursor props in the tracker chart style. Spread onto the Recharts elements. */
export const CHART_GRID = { stroke: '#344155', strokeDasharray: '3 3', vertical: false } as const;
export const CHART_AXIS_TICK = { fill: '#8892b0', fontSize: 11 } as const;
/** BRAND RULE: never the default white or grey hover box on a bar chart. */
export const CHART_CURSOR = { fill: 'rgba(37,99,235,0.08)' } as const;

/**
 * One gradient, as a Recharts-ready <linearGradient>. `area`: the colour at 0.45 opacity at the top
 * fading to 0 at the bottom (the Trends "Stages that moved" fill). `donut`: two stops across the
 * segment, the colour to a deeper shade of itself. Fill with `url(#id)`.
 */
export function chartGradient(id: string, colour: string, kind: 'area' | 'donut' = 'area'): React.ReactElement {
  if (kind === 'donut') {
    return (
      <linearGradient key={id} id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={colour} stopOpacity={1} />
        <stop offset="100%" stopColor={colour} stopOpacity={0.6} />
      </linearGradient>
    );
  }
  return (
    <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor={colour} stopOpacity={0.45} />
      <stop offset="95%" stopColor={colour} stopOpacity={0} />
    </linearGradient>
  );
}

/** chartGradient wrapped in its own <defs>, for a chart with one series. */
export function gradientDefs(id: string, colour: string, kind: 'area' | 'donut' = 'area'): React.ReactElement {
  return <defs>{chartGradient(id, colour, kind)}</defs>;
}

interface TooltipItem {
  name?: string | number;
  value?: number | string | ReadonlyArray<number | string>;
  color?: string;
  dataKey?: string | number;
  payload?: { fill?: string };
}

/**
 * The dark tooltip card as a Recharts `content` component: label in bold, one coloured line per
 * series, values through `format`.
 */
export const ChartTooltipCard: React.FC<{
  active?: boolean;
  payload?: ReadonlyArray<TooltipItem>;
  label?: React.ReactNode;
  format?: (value: number | string) => string;
  labelFormat?: (label: React.ReactNode) => React.ReactNode;
}> = ({ active, payload, label, format, labelFormat }) => {
  if (!active || !payload || payload.length === 0) return null;
  const shownLabel = labelFormat ? labelFormat(label) : label;
  return (
    <div className={TOOLTIP_CARD}>
      {shownLabel !== undefined && shownLabel !== '' && <p className="mb-1 font-bold">{shownLabel}</p>}
      {payload.map((p, i) => {
        const raw = Array.isArray(p.value) ? p.value.join(' to ') : p.value;
        return (
          <p key={String(p.dataKey ?? p.name ?? i)} style={{ color: p.color ?? p.payload?.fill }}>
            {p.name}: <span className="tabular-nums">{format && raw !== undefined ? format(raw as number | string) : raw}</span>
          </p>
        );
      })}
    </div>
  );
};

export { TOOLTIP_CARD };
